// Core type contracts for the in-house AR try-on engine.
// Framework-agnostic: no React/Astro imports here so the engine can be reused
// by the PDP island, the kiosk shell, or a standalone harness.

/**
 * Per-product 3D frame descriptor. Today sourced from a static placeholder
 * (see frames.ts); later populated from Shopify metafields
 * (lens_width / bridge / temple_length / variant images) without touching the
 * engine — this interface is the seam.
 */
export interface FrameAsset {
  id: string;
  name: string;
  glbUrl: string;
  /** Real-world physical dimensions in millimetres (Shopify metafields). */
  lensWidthMm: number; // ancho de lente
  bridgeMm: number; // puente
  templeLengthMm: number; // varilla
  /** Fine-tune placement relative to the nose-bridge anchor, in millimetres. */
  anchorOffsetMm?: { x?: number; y?: number; z?: number };
  /** Extra uniform scale multiplier for art that isn't authored to scale. */
  scaleMultiplier?: number;
  variants?: FrameVariant[];
}

export interface FrameVariant {
  id: string;
  label: string;
  /** CSS color for the swatch dot. */
  swatchColor: string;
  /** Optional per-variant GLB; falls back to the base frame glbUrl. */
  glbUrl?: string;
}

export type DelegatePreference = 'auto' | 'GPU' | 'CPU';
export type TryOnMode = 'web' | 'kiosk';

export interface EngineConfig {
  mode: TryOnMode;
  delegate: DelegatePreference;
  targetFps: number;
  /** Directory containing the self-hosted MediaPipe wasm bundle. */
  wasmBasePath: string;
  /** Path to the self-hosted face_landmarker.task model. */
  modelAssetPath: string;
  /** Mirror the video horizontally (natural selfie view). */
  mirror: boolean;
}

export const DEFAULT_CONFIG: Omit<EngineConfig, 'mode'> = {
  delegate: 'auto',
  targetFps: 30,
  wasmBasePath: '/mediapipe/wasm',
  modelAssetPath: '/mediapipe/face_landmarker.task',
  mirror: true,
};

/** Engine lifecycle states surfaced to the UI. */
export type EngineStatus =
  | 'idle'
  | 'loading' // downloading wasm + model
  | 'starting' // acquiring camera
  | 'searching' // camera live, no face yet
  | 'tracking' // face locked, rendering
  | 'error';

/** Transient quality hints layered on top of `tracking`. */
export type QualityHint =
  | 'ok'
  | 'no-face'
  | 'too-far'
  | 'too-close'
  | 'extreme-angle'
  | 'low-light'
  | 'multi-face';

export type EngineErrorCode =
  | 'unsupported'
  | 'permission-denied'
  | 'no-camera'
  | 'camera-in-use'
  | 'insecure-context'
  | 'webview'
  | 'sdk-load-failed'
  | 'webgl-failed'
  | 'unknown';

export interface EngineError {
  code: EngineErrorCode;
  /** i18n key for a user-facing message. */
  messageKey: string;
  recoverable: boolean;
}

/** Live measurement output (fit intelligence). */
export interface Measurement {
  pdMm: number | null; // pupillary distance
  faceWidthMm: number | null;
  confidence: number; // 0..1 — stability of the median-over-N estimate
  samples: number;
}

export const EMPTY_MEASUREMENT: Measurement = {
  pdMm: null,
  faceWidthMm: null,
  confidence: 0,
  samples: 0,
};

export interface EngineState {
  status: EngineStatus;
  hint: QualityHint;
  error: EngineError | null;
  fps: number;
  measurement: Measurement;
}

export type EngineEvent =
  | { type: 'state'; state: EngineState }
  | { type: 'ready' }
  | { type: 'capture'; dataUrl: string };

export type EngineListener = (event: EngineEvent) => void;
